/* curatedKinasePanels.js
 *
 * Builds the curated kinase GroupTarget specs for the selectivity collection model.
 *
 * Membership is HARDCODED to the curated case lists (the structures that actually
 * have fragment data), NOT derived from otherProperties.dfgConfiguration. The
 * datasource map-list carries an entry for every prepared kinase structure, which
 * is far more than the curated fragment-data set; filtering by dfgConfiguration
 * therefore swept in everything that matched the conformation. The curated lists
 * in curatedKinaseCases.js (generated from tools/kinase-prep/curated-cases/*.txt)
 * are the source of truth: a case appears in a panel only if its structureKey
 * (= structureKey = "project/case") is in one of those lists.
 *
 * Panels (Dan 2026-06-25): DFG-out and DFG-in conformation panels, plus a
 * representative panel of one structure per unique kinase. Out-like is NOT its own
 * panel, but out-like structures still provide last-resort coverage in the
 * representative panel (conformation precedence is read from the curated lists:
 * prefer _altA, then DFG-out > DFG-in > out-like).
 *
 * Pure + store-free; the result is fed to access.addGroupToProjectTargets by the
 * caller. Tests in test/bmaps/curatedKinasePanels.test.js.
 */
import { MapCase } from './MapCase';
import { structureKey } from './structureRef';
import { DFG_IN_CASES, DFG_OUT_CASES, DFG_OUTLIKE_CASES } from './curatedKinaseCases';

/** Stable groupIds for the curated panels. */
export const KinasePanelId = {
    DfgOut: 'kinase:dfg-out',
    DfgIn: 'kinase:dfg-in',
    Representative: 'kinase:representative',
};

/**
 * The curated case lists, as structureKey Sets. The default is the hardcoded
 * fragment-data set; tests pass a synthetic set so they need not pin real data.
 * @typedef {{ dfgIn: Set<string>, dfgOut: Set<string>, dfgOutLike: Set<string> }}
 *   CuratedLists
 */
export const DEFAULT_CURATED_LISTS = {
    dfgIn: new Set(DFG_IN_CASES),
    dfgOut: new Set(DFG_OUT_CASES),
    dfgOutLike: new Set(DFG_OUTLIKE_CASES),
};

const kinaseIdOf = (mapCase) => mapCase.otherProperties?.kinaseId || mapCase.project || '';
const isAltA = (mapCase) => /_altA$/.test(mapCase.case || '');

// Representative conformation precedence, read from curated-list membership (NOT
// otherProperties.dfgConfiguration): DFG-out preferred, then DFG-in, then out-like
// / anything else curated as last-resort coverage.
function dfgRank(key, lists) {
    if (lists.dfgOut.has(key)) return 0;
    if (lists.dfgIn.has(key)) return 1;
    return 2;
}

// Is `a` a better representative for its kinase than the incumbent `b`?
// Order: prefer _altA, then DFG-out > DFG-in > out-like, then a stable case-name tiebreak.
function betterRepresentative(a, b, lists) {
    if (isAltA(a) !== isAltA(b)) return isAltA(a);
    const rankA = dfgRank(structureKey(a), lists);
    const rankB = dfgRank(structureKey(b), lists);
    if (rankA !== rankB) return rankA < rankB;
    return String(a.case) < String(b.case);
}

/** One structure per unique kinaseId, chosen by betterRepresentative. */
function pickRepresentatives(curatedCases, lists) {
    const byKinase = new Map();
    for (const mapCase of curatedCases) {
        const kinaseId = kinaseIdOf(mapCase);
        if (!kinaseId) continue;
        const incumbent = byKinase.get(kinaseId);
        if (!incumbent || betterRepresentative(mapCase, incumbent, lists)) {
            byKinase.set(kinaseId, mapCase);
        }
    }
    return [...byKinase.values()];
}

/**
 * @typedef {{ groupId: string, displayName: string, mapCases: import('./MapCase').MapCase[] }}
 *   CuratedPanel
 */

/**
 * Build the curated kinase panels from a list of available MapCases. Only
 * kinase-library cases (sourceDesc === KinasePrep) that are in a curated list are
 * considered; empty panels are omitted (e.g. when the kinase datasource is not
 * mounted locally, or none of the mounted cases are curated).
 * @param {import('./MapCase').MapCase[]} mapCases
 * @param {CuratedLists} [lists] curated case lists (defaults to the hardcoded set)
 * @returns {CuratedPanel[]}
 */
export function buildCuratedKinasePanels(mapCases = [], lists = DEFAULT_CURATED_LISTS) {
    const curatedKeys = new Set([...lists.dfgIn, ...lists.dfgOut, ...lists.dfgOutLike]);
    const curatedCases = mapCases.filter(
        (mc) => mc.sourceDesc === MapCase.Sources.KinasePrep && curatedKeys.has(structureKey(mc)),
    );
    const inList = (set) => curatedCases.filter((mc) => set.has(structureKey(mc)));
    const panels = [
        {
            groupId: KinasePanelId.DfgOut,
            displayName: 'Kinases: DFG-out',
            mapCases: inList(lists.dfgOut),
        },
        {
            groupId: KinasePanelId.DfgIn,
            displayName: 'Kinases: DFG-in',
            mapCases: inList(lists.dfgIn),
        },
        {
            groupId: KinasePanelId.Representative,
            displayName: 'Kinases: representative (one per kinase)',
            mapCases: pickRepresentatives(curatedCases, lists),
        },
    ];
    return panels.filter((panel) => panel.mapCases.length > 0);
}
